// Helper to fetch and wrap base image as File
async function fetchBaseImageFile(selectedImageUrl: string) {
  const response = await fetch(selectedImageUrl);
  if (!response.ok) throw new Error(`Failed to fetch base image: ${response.status} ${response.statusText}`);
  const baseImageBlob = await response.blob();
  const contentType = baseImageBlob.type || response.headers.get('Content-Type') || 'application/octet-stream';
  let ext = 'bin';
  if (contentType === 'image/png') ext = 'png';
  else if (contentType === 'image/jpeg') ext = 'jpg';
  else if (contentType === 'image/webp') ext = 'webp';
  else if (contentType === 'image/gif') ext = 'gif';
  return {
    file: new File([baseImageBlob], `base_image.${ext}`, { type: contentType }),
    blob: baseImageBlob
  };
}
// Helper to convert inpainting mask data URL to a file for API upload.
async function dataUrlToPngFile(dataUrl: string, filename: string): Promise<File> {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], filename, { type: 'image/png' });
}
// Helper to composite base and print images according to overlay
async function compositeImages({
  baseImageUrl,
  baseImageBlob,
  printImageUrl,
  overlay
}: {
  baseImageUrl?: string,
  baseImageBlob?: Blob,
  printImageUrl: string,
  overlay: { x: number, y: number, width: number, height: number }
}): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const baseImg = new globalThis.Image();
    const printImg = new globalThis.Image();
    let loaded = 0;
    baseImg.crossOrigin = 'anonymous';
    printImg.crossOrigin = 'anonymous';
    baseImg.onload = () => {
      loaded++;
      if (loaded === 2) draw();
    };
    printImg.onload = () => {
      loaded++;
      if (loaded === 2) draw();
    };
    baseImg.onerror = printImg.onerror = (e) => {
      let message = 'Image load error';
      if (e instanceof Error) {
        message = e.message;
      } else if (typeof e === 'string') {
        message = e;
      } else if (e && typeof e === 'object' && 'type' in e) {
        message = `Error type: ${e.type}`;
      }
      reject(new Error(message));
    };
    // Use object URL if Blob is provided
    if (baseImageBlob) {
      const objectUrl = URL.createObjectURL(baseImageBlob);
      baseImg.src = objectUrl;
    } else if (baseImageUrl) {
      baseImg.src = baseImageUrl;
    } else {
      reject(new Error('No base image source'));
      return;
    }
    printImg.src = printImageUrl;
    function draw() {
      const width = baseImg.naturalWidth;
      const height = baseImg.naturalHeight;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('No canvas context'));
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(baseImg, 0, 0, width, height);
      // overlay here is already in base-image pixel coordinates (via mapOverlayToImage),
      // so we use it directly without additional scaling.
      const mappedOverlay = {
        x: overlay.x,
        y: overlay.y,
        width: overlay.width,
        height: overlay.height
      };
      ctx.globalAlpha = 1;

      // Clip to the overlay rectangle so that only the rescaled print region appears,
      // matching what the user sees after resizing.
      ctx.save();
      ctx.beginPath();
      ctx.rect(mappedOverlay.x, mappedOverlay.y, mappedOverlay.width, mappedOverlay.height);
      ctx.clip();

      // Draw print image inside the overlay while preserving its original aspect ratio.
      // This mirrors the on-screen behaviour (object-fit: contain) so the shape is not distorted.
      const pw = printImg.naturalWidth;
      const ph = printImg.naturalHeight;
      const printAspect = pw / ph;
      const overlayAspect = mappedOverlay.width / mappedOverlay.height;

      let destWidth: number;
      let destHeight: number;
      if (printAspect > overlayAspect) {
        // Print image is proportionally wider than the overlay: fit to overlay width
        destWidth = mappedOverlay.width;
        destHeight = mappedOverlay.width / printAspect;
      } else {
        // Print image is proportionally taller than the overlay: fit to overlay height
        destHeight = mappedOverlay.height;
        destWidth = mappedOverlay.height * printAspect;
      }

      const destX = mappedOverlay.x + (mappedOverlay.width - destWidth) / 2;
      const destY = mappedOverlay.y + (mappedOverlay.height - destHeight) / 2;

      ctx.drawImage(
        printImg,
        0,
        0,
        pw,
        ph,
        destX,
        destY,
        destWidth,
        destHeight
      );

      // Restore clipping region for any further drawing
      ctx.restore();
      ctx.globalAlpha = 1;
      canvas.toBlob(blob => {
        if (baseImageBlob) {
          URL.revokeObjectURL(baseImg.src);
        }
        if (blob) resolve(blob);
        else reject(new Error('Failed to export canvas'));
      }, 'image/png');
    }
  });
}
import React, { useRef, useState, useEffect } from 'react';
import { ImageHeader } from './ImageHeader';
import { BrushSizeSlider } from './BrushSizeSlider';
import { InpaintingCanvas, type InpaintingCanvasRef } from './InpaintingCanvas';
import { ChatInput } from '../chat/ChatInput';
import { useImageState, useImageActions } from '../../contexts/ImageContext';
import { Message } from '../../types';
import { LocalInpaintingUpload } from './LocalInpaintingUpload';

interface ImageViewerProps {
  readonly onClose: () => void;
  readonly onSendMessage: (message: string, files?: File[], isInpaintingMode?: boolean) => void;
  readonly onNewChat: () => void;
  readonly isTyping: boolean;
  readonly messages: Message[];
  readonly readOnly?: boolean;
  readonly isLocalInpaintingMode?: boolean;
  /*Local blob URL of the image*/
  readonly localUploadImageUrl?: string;
  /* Called by LocalInpaintingUpload once the user selects a file. */
  readonly onLocalImageUpload?: (url: string) => void;
}

export function ImageViewer({ onClose, onSendMessage, onNewChat, isTyping, messages, readOnly = false, isLocalInpaintingMode = false, localUploadImageUrl, onLocalImageUpload }: ImageViewerProps) {
  // Listen for Print Scale overlay reset event from ImageHeader
  useEffect(() => {
    const handleResetPrintScale = () => {
      setPrintScaleImage(null);
      setOverlay({
        x: 0.25,
        y: 0.25,
        width: 0.5,
        height: 0.5,
        dragging: false,
        resizing: false,
        resizeCorner: null,
        dragStart: { x: 0, y: 0 },
        overlayStart: { x: 0.25, y: 0.25, width: 0.5, height: 0.5 }
      });
    };
      globalThis.addEventListener('resetPrintScaleOverlay', handleResetPrintScale);
    return () => {
        globalThis.removeEventListener('resetPrintScaleOverlay', handleResetPrintScale);
    };
  }, []);
  const canvasRef = useRef<InpaintingCanvasRef>(null);
  const [brushColor, setBrushColor] = useState<'red' | 'blue' | 'green'>('red');
  const { selectedImage, selectedImageMessageId, inpainting } = useImageState();
  const { setBrushSize, setMaskData, setInpaintingActive, clearInpainting } = useImageActions();

  // Print Scale overlay state
  const [printScaleImage, setPrintScaleImage] = useState<string | null>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const imageElementRef = useRef<HTMLImageElement>(null);
  // Overlay state: position (x, y), size (width, height), dragging, resizing
    type ResizeCorner = 'tl' | 'tr' | 'bl' | 'br';
    const [overlay, setOverlay] = useState<{
      x: number;
      y: number;
      width: number;
      height: number;
      dragging: boolean;
      resizing: boolean;
      resizeCorner: null | ResizeCorner;
      dragStart: { x: number; y: number };
      overlayStart: { x: number; y: number; width: number; height: number };
    }>({
      x: 0.25, // Centered horizontally (25% left)
      y: 0.25, // Centered vertically (25% top)
      width: 0.5, // 50% of base image width
      height: 0.5, // 50% of base image height
      dragging: false,
      resizing: false,
      resizeCorner: null,
      dragStart: { x: 0, y: 0 },
      overlayStart: { x: 0.25, y: 0.25, width: 0.5, height: 0.5 }
    });

  // Clear print scale image when inpainting is activated
  useEffect(() => {
    if (inpainting.isActive && printScaleImage) {
      setPrintScaleImage(null);
    }
  }, [inpainting.isActive]);

  // Clear print scale image when a new base image is selected
  useEffect(() => {
    setPrintScaleImage(null);
  }, [selectedImage, selectedImageMessageId]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Exit inpainting mode with Escape key
      if (e.key === 'Escape' && inpainting.isActive && !readOnly) {
        e.preventDefault();
        // Clear any inpainting data and exit inpainting mode
        canvasRef?.current?.clearMask();
        clearInpainting();
        setInpaintingActive(false);
      }
    };

    // Add event listener to window to catch keyboard events
      globalThis.addEventListener('keydown', handleKeyDown);
    
    // Cleanup event listener on unmount
    return () => {
        globalThis.removeEventListener('keydown', handleKeyDown);
    };
  }, [inpainting.isActive, readOnly, setInpaintingActive, clearInpainting]);

  const handleBrushSizeChange = (size: number) => {
    setBrushSize(size);
  };

  const handleMaskUpdate = (maskData: string) => {
    setMaskData(maskData);
  };

  const handleBrushColorChange = (color: 'red' | 'blue' | 'green') => {
    setBrushColor(color);
  };

  const setSelectedImageURL = () => {
    const selectedMessage = messages.find(msg => msg.id === selectedImageMessageId);
    if (selectedMessage?.imageUrls) {
      return selectedMessage?.imageUrls[selectedImage];
    }
    return '';
  };

  const selectedImageUrl = selectedImage === -1 ? '' : setSelectedImageURL();

  // In local-upload mode, use local blob URL instead of an AI-generated attachment URL
  const effectiveImageUrl = isLocalInpaintingMode ? (localUploadImageUrl ?? '') : selectedImageUrl;

  const printScaleInputRef = useRef<HTMLInputElement>(null);

  const handlePrintScaleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPrintScaleImage(ev.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Print Scale button handler for ImageHeader
  const handlePrintScale = () => {
    if (printScaleInputRef.current) {
      printScaleInputRef.current.value = '';
      printScaleInputRef.current.click();
    }
  };

  // Mouse move and up handlers for drag/resize (MUST be above return)
  useEffect(() => {
    let isDragging = overlay.dragging;
    let isResizing = overlay.resizing;
    let resizeCorner = overlay.resizeCorner;

    function clamp(val: number, min: number, max: number) {
      return Math.max(min, Math.min(val, max));
    }

    function getNewPosition(dx: number, dy: number, overlayStart: any, rect: DOMRect, width: number, height: number) {
      let newX = overlayStart.x + dx / rect.width;
      let newY = overlayStart.y + dy / rect.height;
      newX = clamp(newX, 0, 1 - width);
      newY = clamp(newY, 0, 1 - height);
      return { x: newX, y: newY };
    }

    function getResizeValues(corner: string, dx: number, dy: number, overlayStart: any, rect: DOMRect) {
      let { width, height, x, y } = overlayStart;
      // Proportional (diagonal) scaling: use the larger movement (dx or dy) and apply equally to width and height
      let delta = Math.max(Math.abs(dx), Math.abs(dy));
      let sign = 1;
      switch (corner) {
        case 'tl':
          sign = (dx < 0 && dy < 0) ? 1 : -1;
          width += sign * delta / rect.width;
          height += sign * delta / rect.height;
          x -= sign * delta / rect.width;
          y -= sign * delta / rect.height;
          break;
        case 'tr':
          sign = (dx > 0 && dy < 0) ? 1 : -1;
          width += sign * delta / rect.width;
          height += sign * delta / rect.height;
          y -= sign * delta / rect.height;
          break;
        case 'bl':
          sign = (dx < 0 && dy > 0) ? 1 : -1;
          width += sign * delta / rect.width;
          height += sign * delta / rect.height;
          x -= sign * delta / rect.width;
          break;
        case 'br':
          sign = (dx > 0 && dy > 0) ? 1 : -1;
          width += sign * delta / rect.width;
          height += sign * delta / rect.height;
          break;
      }
  // Enforce proportional (square) overlay
  let size = Math.min(width, height);
  size = clamp(size, 0.1, 1);
  width = height = size;
  // Clamp position so overlay stays within bounds
  x = clamp(x, 0, 1 - size);
  y = clamp(y, 0, 1 - size);
  return { width, height, x, y };
    }

    const handleMouseMove = (e: MouseEvent) => {
      const parent = imageContainerRef.current;
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      const dx = e.clientX - overlay.dragStart.x;
      const dy = e.clientY - overlay.dragStart.y;
      if (isDragging) {
        const { x, y } = getNewPosition(dx, dy, overlay.overlayStart, rect, overlay.width, overlay.height);
        setOverlay(prev => ({ ...prev, x, y }));
      } else if (isResizing && resizeCorner) {
        const { width, height, x, y } = getResizeValues(resizeCorner, dx, dy, overlay.overlayStart, rect);
        setOverlay(prev => ({ ...prev, width, height, x, y }));
      }
    };
    function endOverlayInteraction() {
      setOverlay(prev => ({ ...prev, dragging: false, resizing: false, resizeCorner: null }));
      isDragging = false;
      isResizing = false;
    }
    const handleMouseUp = () => {
      if (isDragging || isResizing) endOverlayInteraction();
    };

    // Touch support
    const handleTouchMove = (e: TouchEvent) => {
      const parent = imageContainerRef.current;
      if (!parent || e.touches.length !== 1) return;
      const touch = e.touches[0];
      const rect = parent.getBoundingClientRect();
      const dx = touch.clientX - overlay.dragStart.x;
      const dy = touch.clientY - overlay.dragStart.y;
      if (isDragging) {
        const { x, y } = getNewPosition(dx, dy, overlay.overlayStart, rect, overlay.width, overlay.height);
        setOverlay(prev => ({ ...prev, x, y }));
      } else if (isResizing && resizeCorner) {
        const { width, height, x, y } = getResizeValues(resizeCorner, dx, dy, overlay.overlayStart, rect);
        setOverlay(prev => ({ ...prev, width, height, x, y }));
      }
    };
    const handleTouchEnd = () => {
      if (isDragging || isResizing) endOverlayInteraction();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [overlay.dragging, overlay.resizing, overlay.resizeCorner, overlay.dragStart, overlay.overlayStart, overlay.width, overlay.height]);

  interface OverlayRect {
    x: number;
    y: number;
    width: number;
    height: number;
  }

  // Helper to map overlay (container-relative) to image-relative coordinates
  function mapOverlayToImage(
    overlay: OverlayRect,
    container: HTMLDivElement,
    img: HTMLImageElement
  ): OverlayRect {
    // Overlay is stored normalized 0-1 relative to the *container* dimensions.
    // Convert from container space to the actual rendered image rect (object-contain),
    // then normalize to image space and finally to natural pixel coordinates.

    const containerRect = container.getBoundingClientRect();
    const imgAspect = img.naturalWidth / img.naturalHeight;
    const containerAspect = containerRect.width / containerRect.height;

    let renderedWidth: number;
    let renderedHeight: number;
    let renderedLeft: number;
    let renderedTop: number;

    if (imgAspect > containerAspect) {
      // Image is wider than container
      renderedWidth = containerRect.width;
      renderedHeight = renderedWidth / imgAspect;
      renderedLeft = (containerRect.width - renderedWidth) / 2;
      renderedTop = (containerRect.height - renderedHeight) / 2;
    } else {
      // Image is taller than container
      renderedHeight = containerRect.height;
      renderedWidth = renderedHeight * imgAspect;
      renderedTop = (containerRect.height - renderedHeight) / 2;
      renderedLeft = (containerRect.width - renderedWidth) / 2;
    }

    // Convert overlay from normalized container space to container pixels
    const overlayLeftPx = overlay.x * containerRect.width;
    const overlayTopPx = overlay.y * containerRect.height;
    const overlayWidthPx = overlay.width * containerRect.width;
    const overlayHeightPx = overlay.height * containerRect.height;

    // Convert container pixels to rendered-image-relative pixels, then to normalized image coords
    const normX = (overlayLeftPx - renderedLeft) / renderedWidth;
    const normY = (overlayTopPx - renderedTop) / renderedHeight;
    const normW = overlayWidthPx / renderedWidth;
    const normH = overlayHeightPx / renderedHeight;

    const imgWidth = img.naturalWidth;
    const imgHeight = img.naturalHeight;

    return {
      x: normX * imgWidth,
      y: normY * imgHeight,
      width: normW * imgWidth,
      height: normH * imgHeight
    };
  }

  const buildPrintScaleCompositeFile = async (baseImageBlob: Blob): Promise<File | null> => {
    if (!printScaleImage || !imageContainerRef.current || !imageElementRef.current) {
      return null;
    }

    const imgEl = imageElementRef.current;
    const containerEl = imageContainerRef.current;

    // Ensure the image has fully loaded and natural dimensions are available.
    if (!imgEl.complete || imgEl.naturalWidth <= 0 || imgEl.naturalHeight <= 0) {
      console.warn('Base image not fully loaded; skipping composite generation.');
      return null;
    }

    const mappedOverlay = mapOverlayToImage(overlay, containerEl, imgEl);
    const compositeBlob = await compositeImages({
      baseImageBlob,
      printImageUrl: printScaleImage,
      overlay: mappedOverlay,
    });

    return new File([compositeBlob], 'composite_image.png', { type: 'image/png' });
  };

  const handleLocalInpaintingSend = async (
    msg: string,
    files: File[] | undefined,
    isInpaintingMode: boolean | undefined,
  ) => {
    if (!effectiveImageUrl) {
      // Prevent submitting an empty message when the local image hasn't been selected yet.
      if (msg.trim() || (files?.length ?? 0) > 0) {
        onSendMessage(msg, files, false);
        onClose();
      }
      return;
    }

    try {
      const { file: baseImageFile, blob: baseImageBlob } = await fetchBaseImageFile(effectiveImageUrl);
      let compositeFile: File | null = null;
      try {
        compositeFile = await buildPrintScaleCompositeFile(baseImageBlob);
      } catch (compositeError) {
        console.error('Failed to build print-scale composite in local mode:', compositeError);
      }

      const sendFiles: File[] = [baseImageFile];
      if (inpainting.maskData) {
        const maskFile = await dataUrlToPngFile(inpainting.maskData, 'inpainting_mask.png');
        sendFiles.push(maskFile);
      }
      if (compositeFile) {
        sendFiles.push(compositeFile);
      }
      if (files?.length) {
        sendFiles.push(...files);
      }

      onSendMessage(msg, sendFiles, Boolean(inpainting.maskData));
      onClose();
    } catch (e) {
      console.error('Failed to prepare local inpainting files:', e);
      onSendMessage(msg, files, isInpaintingMode);
    }
  };

  return (
    <div className="flex flex-col w-1/2 border-l border-gray-200 dark:border-gray-700">
      {/* ImageHeader component with inpainting tools and close button */}
      <ImageHeader 
        onClose={onClose}
        readOnly={readOnly}
        getSelectedImageUrl={setSelectedImageURL}
        canvasRef={canvasRef}
        brushColor={brushColor}
        onBrushColorChange={handleBrushColorChange}
        onPrintScale={handlePrintScale}
        isPrintScaleActive={!!printScaleImage}
      />

      {/* BrushSizeSlider component - separate from ImageHeader */}
      <BrushSizeSlider
        brushSize={inpainting.brushSize}
        onBrushSizeChange={handleBrushSizeChange}
        isActive={inpainting.isActive}
        minSize={5}
        maxSize={100}
        readOnly={readOnly}
      />

      {/* Image display area with inpainting canvas overlay */}
      <div className="flex justify-center items-center overflow-hidden w-full flex-1 py-1 sm:py-2 md:py-4 lg:py-6 xl:py-8">
              {/* Empty upload state - shown when opened from sidebar but no file chosen yet */}
        {isLocalInpaintingMode && !effectiveImageUrl && onLocalImageUpload && (
          <LocalInpaintingUpload onImageSelected={onLocalImageUpload} />
        )}

        {effectiveImageUrl && (
          <div
            ref={imageContainerRef}
            className="relative flex justify-center items-center w-full h-full"
          >
            {/* Base image */}
            <img
              ref={imageElementRef}
              src={effectiveImageUrl}
              alt="Selected content for editing"
              className="max-w-full max-h-full object-contain"
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              style={{
                userSelect: 'none',
                pointerEvents: inpainting.isActive ? 'none' : 'auto'
              }}
            />

            {/* Interactive Print Scale overlay - only show if not inpainting */}
            {printScaleImage && !inpainting.isActive && (
              <React.Fragment>
                {/* Overlay bounding box kept in container-relative percentages so that
                    drag/resize math (which uses the container rect) and the visual
                    representation share the same coordinate space. */}
                <fieldset
                  className="absolute"
                  aria-label="Print scale overlay"
                  style={{
                    left: `${overlay.x * 100}%`,
                    top: `${overlay.y * 100}%`,
                    width: `${overlay.width * 100}%`,
                    height: `${overlay.height * 100}%`,
                    zIndex: 15,
                    cursor: overlay.dragging ? 'move' : 'pointer',
                    outline: 'none',
                  }}
              >
                <button
                  type="button"
                  aria-label="Move print scale overlay"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'transparent',
                    zIndex: 16,
                    cursor: overlay.dragging ? 'move' : 'pointer',
                    outline: 'none',
                    border: 'none',
                    padding: 0
                  }}
                  onMouseDown={e => {
                    if (!overlay.resizing && e.button === 0) {
                      setOverlay(prev => ({
                        ...prev,
                        dragging: true,
                        dragStart: { x: e.clientX, y: e.clientY },
                        overlayStart: { x: prev.x, y: prev.y, width: prev.width, height: prev.height }
                      }));
                    }
                  }}
                  onKeyDown={e => {
                    // Arrow keys move overlay
                    if (!overlay.resizing && !overlay.dragging) {
                      let step = 0.01;
                      if (e.key === 'ArrowLeft') {
                        setOverlay(prev => ({ ...prev, x: Math.max(prev.x - step, 0) }));
                        e.preventDefault();
                      } else if (e.key === 'ArrowRight') {
                        setOverlay(prev => ({ ...prev, x: Math.min(prev.x + step, 1 - prev.width) }));
                        e.preventDefault();
                      } else if (e.key === 'ArrowUp') {
                        setOverlay(prev => ({ ...prev, y: Math.max(prev.y - step, 0) }));
                        e.preventDefault();
                      } else if (e.key === 'ArrowDown') {
                        setOverlay(prev => ({ ...prev, y: Math.min(prev.y + step, 1 - prev.height) }));
                        e.preventDefault();
                      }
                    }
                  }}
                  onTouchStart={e => {
                    if (!overlay.resizing && e.touches.length === 1) {
                      const touch = e.touches[0];
                      setOverlay(prev => ({
                        ...prev,
                        dragging: true,
                        dragStart: { x: touch.clientX, y: touch.clientY },
                        overlayStart: { x: prev.x, y: prev.y, width: prev.width, height: prev.height }
                      }));
                    }
                  }}
                >
                {/* Dashed bounding box */}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: '2px dashed #6366f1',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                    pointerEvents: 'none'
                  }}
                />
                {/* Print image */}
                <img
                  src={printScaleImage}
                  alt="Print Scale Overlay"
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    opacity: 1,
                    borderRadius: '8px',
                    pointerEvents: 'none',
                    objectFit: 'contain',
                    background: 'transparent',
                  }}
                />
                {/* Corner handles for proportional resize */}
                {/* Helper functions for resize logic */}
                {(['tl', 'tr', 'bl', 'br'] as ('tl' | 'tr' | 'bl' | 'br')[]).map((corner) => {
                  const handleResizeStart = (clientX: number, clientY: number) => {
                    setOverlay(prev => ({
                      ...prev,
                      resizing: true,
                      resizeCorner: corner,
                      dragStart: { x: clientX, y: clientY },
                      overlayStart: { width: prev.width, height: prev.height, x: prev.x, y: prev.y }
                    }));
                  };

                  const getResizedOverlay = (
                    overlay: { width: number; height: number; x: number; y: number },
                    corner: 'tl' | 'tr' | 'bl' | 'br',
                    key: string
                  ): { width: number; height: number; x: number; y: number } => {
                    const step = 0.01;
                    const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max);
                    const minSize = 0.1, maxSize = 1;
                    let { width, height, x, y } = overlay;
                    switch (key) {
                      case 'ArrowLeft':
                        if (corner === 'tl' || corner === 'bl') {
                          width = clamp(width + step, minSize, maxSize);
                          x = clamp(x - step, 0, 1 - width);
                        }
                        break;
                      case 'ArrowRight':
                        if (corner === 'tr' || corner === 'br') {
                          width = clamp(width + step, minSize, maxSize);
                        }
                        break;
                      case 'ArrowUp':
                        if (corner === 'tl' || corner === 'tr') {
                          height = clamp(height + step, minSize, maxSize);
                          y = clamp(y - step, 0, 1 - height);
                        }
                        break;
                      case 'ArrowDown':
                        if (corner === 'bl' || corner === 'br') {
                          height = clamp(height + step, minSize, maxSize);
                        }
                        break;
                      default:
                        break;
                    }
                    x = clamp(x, 0, 1 - width);
                    y = clamp(y, 0, 1 - height);
                    return { width, height, x, y };
                  };

                  const handleKeyboardResize = (e: React.KeyboardEvent<HTMLButtonElement>) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleResizeStart(0, 0);
                      e.preventDefault();
                      return;
                    }
                    if (overlay.resizing && overlay.resizeCorner === corner) {
                      const resized = getResizedOverlay(overlay, corner, e.key);
                      setOverlay(prev => ({ ...prev, ...resized }));
                      e.preventDefault();
                      return;
                    }
                    if (e.key === 'Escape' && overlay.resizing && overlay.resizeCorner === corner) {
                      setOverlay(prev => ({ ...prev, resizing: false, resizeCorner: null }));
                      e.preventDefault();
                    }
                  };

                  return (
                    <button
                      key={corner}
                      type="button"
                      aria-label={`Resize from ${corner}`}
                      style={{
                        position: 'absolute',
                        width: 16,
                        height: 16,
                        background: '#fff',
                        border: '2px solid #6366f1',
                        borderRadius: '50%',
                        zIndex: 20,
                        cursor: 'nwse-resize',
                        outline: 'none',
                        ...(corner === 'tl' && { left: -8, top: -8 }),
                        ...(corner === 'tr' && { right: -8, top: -8 }),
                        ...(corner === 'bl' && { left: -8, bottom: -8 }),
                        ...(corner === 'br' && { right: -8, bottom: -8 })
                      }}
                      onMouseDown={e => {
                        e.stopPropagation();
                        handleResizeStart(e.clientX, e.clientY);
                      }}
                      onKeyDown={handleKeyboardResize}
                      onTouchStart={e => {
                        if (e.touches.length === 1) {
                          const touch = e.touches[0];
                          handleResizeStart(touch.clientX, touch.clientY);
                        }
                      }}
                    />
                  );
                })}
                </button>
                </fieldset>
              </React.Fragment>
            )}

            {/* Inpainting canvas overlay - positioned exactly over the image */}
            {inpainting.isActive && (
              <InpaintingCanvas
                ref={canvasRef}
                imageUrl={effectiveImageUrl}
                brushSize={inpainting.brushSize}
                isEraserMode={inpainting.isEraserMode}
                brushColor={brushColor}
                onMaskUpdate={handleMaskUpdate}
                className="absolute inset-0"
                disabled={readOnly}
              />
            )}
          </div>
        )}
      </div>

      {/* Input moved to image panel when in half-width mode */}
      <div className="border-t border-gray-200 dark:border-gray-700">
        <ChatInput
          onSendMessage={async (msg, files, isInpaintingMode) => {
            // Local inpainting must send base image + mask through the existing pipeline.
            if (isLocalInpaintingMode) {
              await handleLocalInpaintingSend(msg, files, isInpaintingMode);
              return;
            }

            if (!printScaleImage || !effectiveImageUrl) {
              onSendMessage(msg, files, isInpaintingMode);
              return;
            }
            try {
              const { file: baseImageFile, blob: baseImageBlob } = await fetchBaseImageFile(effectiveImageUrl);
              let compositeFile: File | null = null;
              try {
                compositeFile = await buildPrintScaleCompositeFile(baseImageBlob);
              } catch (compositeError) {
                console.error('Failed to build print-scale composite:', compositeError);
              }
              const sendFiles = [baseImageFile, compositeFile].filter(Boolean) as File[];
              onSendMessage(msg, sendFiles, isInpaintingMode);
            } catch (e) {
              console.error('Failed to send images:', e);
              onSendMessage(msg, files, isInpaintingMode);
            }
          }}
          disabled={isTyping}
          onNewChat={onNewChat}
          messages={messages}
          imageViewerMode={true}
          printScaleImage={printScaleImage}
        />
      </div>

      {/* Hidden file input for print scale upload */}
      {!inpainting.isActive && (
        <input
          type="file"
          accept="image/*"
          ref={printScaleInputRef}
          style={{ display: 'none' }}
          onChange={handlePrintScaleUpload}
        />
      )}
    </div>
  );
}
